import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Heading,
  Divider,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  Link,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { FiMessageCircle } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { authApi } from '../services/api';

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !email || !password) {
      setError('Preencha todos os campos');
      return;
    }
    if (password.length < 8) {
      setError('Senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    try {
      setIsLoading(true);
      const result = await authApi.register({ name: name.trim(), email, password });
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message || 'Erro ao registrar');
      }
    } catch {
      setError('Erro ao conectar ao servidor');
    } finally {
      setIsLoading(false);
    }
  };

  // Tela de confirmação após cadastro bem-sucedido
  if (success) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
        <Container maxW="md">
          <Card bg={cardBg} shadow="xl">
            <CardBody p={8}>
              <VStack spacing={6} align="center">
                <Box color="green.500">
                  <CheckCircleIcon boxSize={16} />
                </Box>
                <Heading size="md" textAlign="center" color="gray.700">
                  Cadastro enviado com sucesso!
                </Heading>
                <Text color="gray.600" textAlign="center">
                  Seu cadastro foi recebido e está aguardando aprovação do administrador.
                  Você receberá acesso assim que sua solicitação for aprovada.
                </Text>
                <Divider />
                <Link as={RouterLink} to="/" color="brand.600" fontWeight="semibold">
                  Voltar para o login
                </Link>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
      <Container maxW="md">
        <Card bg={cardBg} shadow="xl">
          <CardBody p={8}>
            <VStack spacing={6} align="center">
              {/* Header */}
              <VStack spacing={2}>
                <Box
                  p={3}
                  bg="brand.500"
                  color="white"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiMessageCircle size="24px" />
                </Box>
                <Heading size="lg" color="brand.600" textAlign="center">
                  Solicitar Acesso
                </Heading>
                <Text color="gray.600" textAlign="center" fontSize="sm">
                  Preencha o formulário. O administrador irá aprovar seu acesso.
                </Text>
              </VStack>

              <Divider />

              <Box w="100%">
                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Nome completo</FormLabel>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Seu nome"
                        bg={inputBg}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        bg={inputBg}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Senha</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Mínimo 8 caracteres"
                          bg={inputBg}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Confirmar senha</FormLabel>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Repita a senha"
                        bg={inputBg}
                      />
                    </FormControl>

                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      width="100%"
                      isLoading={isLoading}
                      loadingText="Enviando..."
                    >
                      Solicitar acesso
                    </Button>
                  </VStack>
                </form>
              </Box>

              <Divider />

              <Text color="gray.500" fontSize="sm">
                Já tem acesso?{' '}
                <Link as={RouterLink} to="/" color="brand.600" fontWeight="semibold">
                  Fazer login
                </Link>
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default Register;
